import { useEffect, useId, useState } from 'react'
import { DateTime } from 'luxon'
import { useQuery } from '@tanstack/react-query'
import { X, Search } from 'lucide-react'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { TextField, TextAreaField, FieldLabel, DateField } from './ui/Field'
import { Avatar } from './Avatar'
import { api } from '../lib/api'
import {
  goalAssignableDepartmentsResponse,
  goalAssignableOwnersResponse,
} from '../lib/types'
import {
  STATUS_OPTIONS,
  TARGET_TYPES,
  TARGET_LABEL,
  newKeyResult,
} from '../lib/goals-ui.jsx'

const num = (v, fallback = null) =>
  v === '' || v == null || !Number.isFinite(Number(v)) ? fallback : Number(v)

/** A key result's target must be a POSITIVE number — blank, 0 or a negative
 *  value is not a measurable target (progress is current ÷ target). */
const targetInvalid = (t) => !(num(t.target, null) > 0)

/** Whole-day span between two 'YYYY-MM-DD' dates (b − a). */
const daySpan = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / 86400000)

/** Blank form seed for a new goal (period dates supplied by the caller). */
export function emptyGoalForm({ startDate = '', targetDate = '' } = {}) {
  return {
    name: '',
    description: '',
    priority: 'normal',
    startDate,
    targetDate,
    manualStatus: 'not_started',
    keyResults: [],
    tags: [],
  }
}

/** Seed a form from an existing goal object (for editing). */
export function goalToForm(goal) {
  return {
    name: goal.name ?? '',
    description: goal.description ?? '',
    priority: goal.priority ?? 'normal',
    startDate: goal.startDate ?? '',
    targetDate: goal.targetDate ?? '',
    manualStatus: goal.manualStatus ?? 'not_started',
    ownerId: goal.ownerId ?? null,
    ownerName: goal.ownerName ?? null,
    tags: goal.tags ?? [],
    keyResults: (goal.keyResults ?? []).map((t) => ({
      id: t.id,
      type: t.type,
      name: t.name ?? '',
      start: t.start ?? 0,
      target: t.target ?? (t.type === 'percentage' ? 100 : ''),
      current: t.current ?? 0,
    })),
  }
}

/**
 * Shared create/edit form for a goal (and sub-goal). Holds its own field state,
 * validates, and hands the API-shaped payload to `onSubmit`; the parent runs the
 * mutation and closes on success.
 *
 * Assignee (create only, and only when `canAssign` — Managers / Admins): a goal
 * defaults to its creator. Choosing "Someone else" reveals a guided two-step
 * picker — department (step 1) then a member of it (step 2) — and sends the
 * chosen member as `ownerId`. The server (`resolveOwnerId`) re-enforces who may
 * be assigned, so this is a UI convenience over an already-gated field.
 */
export function GoalFormModal({
  open,
  onClose,
  onSubmit,
  saving = false,
  title = 'Add Goal',
  submitLabel = 'Add Goal',
  type = 'weekly',
  initial,
  // True only when EDITING an existing goal. Gates the "unchanged past target"
  // exemption below: on create, `initial` is just the seeded period default
  // (which can be a past week's end), so it must NOT be treated as a
  // pre-existing target — otherwise a new goal on a past week slips past the
  // "target can't be in the past" check.
  isEdit = false,
  // True when the Add form was opened on the CURRENT-period board. The current
  // period is a valid target even once its working week has ended (weekends),
  // so the unchanged pre-filled target is exempt from the "not in the past"
  // check — matching the server's period-based guard. Past-period boards pass
  // false, so backdated creation stays blocked (the reported bug).
  isCurrentPeriod = false,
  // True only for actors who may assign a goal to someone else (Managers /
  // Admins — see `canAssignGoals`). Gates the create-time Assignee picker; the
  // server re-enforces assignability regardless.
  canAssign = false,
}) {
  // Per-mount id prefix for the key-result label/input pairs. `useId()`
  // rather than a literal: the modal can mount more than once and duplicate
  // DOM ids would cross-wire the labels.
  const uid = useId()

  const [form, setForm] = useState(initial ?? emptyGoalForm())
  // Inline field-validation errors, shown BELOW each field (via the shared Field
  // components) for consistency with the rest of the app + the QA standard.
  // Cleared on reopen and as the user edits the offending field.
  const [errors, setErrors] = useState({})

  // Assignee (create-only). Default is the creator ("Myself"); a Manager/Admin
  // may instead assign the goal to a member via the two-step department → person
  // picker below. Only sent as `ownerId` when a specific member is chosen.
  const showAssignee = canAssign && !isEdit
  const [assignMode, setAssignMode] = useState('self') // 'self' | 'other'
  const [assigneeDeptId, setAssigneeDeptId] = useState('')
  const [assignee, setAssignee] = useState(null) // { id, fullName } | null
  const [ownerSearch, setOwnerSearch] = useState('')
  const [ownerSearchDebounced, setOwnerSearchDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setOwnerSearchDebounced(ownerSearch.trim()), 250)
    return () => clearTimeout(t)
  }, [ownerSearch])

  // Step 1 — the departments this actor may assign into (company-wide → all;
  // a department manager → only their own). Loaded lazily when the picker opens.
  const departmentsQuery = useQuery({
    queryKey: ['goal-assignable-departments'],
    queryFn: async () =>
      goalAssignableDepartmentsResponse.parse(
        (await api.get('/goals/assignable-departments')).data,
      ).data,
    enabled: open && showAssignee && assignMode === 'other',
  })
  const assignDepartments = departmentsQuery.data ?? []

  // Step 2 — the members of the chosen department the actor may assign to
  // (rank-filtered server-side). Server-side search over the whole department,
  // not just a capped first page.
  const ownersQuery = useQuery({
    queryKey: ['goal-assignable-owners-create', assigneeDeptId, ownerSearchDebounced],
    queryFn: async () =>
      goalAssignableOwnersResponse.parse(
        (
          await api.get('/goals/assignable-owners', {
            params: {
              departmentId: assigneeDeptId,
              q: ownerSearchDebounced || undefined,
              limit: 25,
            },
          })
        ).data,
      ).data,
    enabled:
      open && showAssignee && assignMode === 'other' && Boolean(assigneeDeptId),
  })
  const ownerCandidates = ownersQuery.data ?? []

  // Target date is a DEADLINE — it must be today or later (a past deadline is
  // an already-missed goal at creation). Compared as 'YYYY-MM-DD' strings so it
  // lines up with the native date inputs. An UNCHANGED pre-existing past target
  // (editing a goal from a past period) is exempt so its other fields can still
  // save. Start date is deliberately NOT floored — a current-period goal often
  // starts a few days ago (e.g. Monday of this week).
  const todayIso = DateTime.local().toISODate()
  const initialTargetDate = initial?.targetDate ?? ''
  // Picker floor for Target. On CREATE: the later of today and the chosen Start
  // (a new goal can't have an already-past deadline). On EDIT: floor at Start
  // only (never today), so a historical goal from a past period stays saveable
  // — the "changed to a past date" case is still caught by validate() + the
  // server. Without this, the native `min` would block editing old goals.
  const targetMinIso = isEdit || isCurrentPeriod
    ? form.startDate || undefined
    : form.startDate && form.startDate > todayIso
      ? form.startDate
      : todayIso

  // Re-seed whenever the modal opens (add vs edit) and reset the Assignee picker
  // back to "Myself" so a previous session's selection never leaks in.
  useEffect(() => {
    if (!open) return
    setForm(initial ?? emptyGoalForm())
    setErrors({})
    setAssignMode('self')
    setAssigneeDeptId('')
    setAssignee(null)
    setOwnerSearch('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const addKr = (t) => setForm((f) => ({ ...f, keyResults: [...(f.keyResults ?? []), newKeyResult(t)] }))
  const updateKr = (i, patch) => {
    setForm((f) => ({ ...f, keyResults: (f.keyResults ?? []).map((t, idx) => (idx === i ? { ...t, ...patch } : t)) }))
    if (errors.keyResults) setErrors((x) => ({ ...x, keyResults: undefined }))
  }
  const removeKr = (i) => {
    setForm((f) => ({ ...f, keyResults: (f.keyResults ?? []).filter((_, idx) => idx !== i) }))
    if (errors.keyResults) setErrors((x) => ({ ...x, keyResults: undefined }))
  }

  function validate() {
    const next = {}
    const trimmedName = form.name.trim()
    if (trimmedName.length === 0) {
      next.name = 'Goal Name is required.'
    } else if (trimmedName.length < 2) {
      next.name = 'Goal Name must be at least 2 characters.'
    }
    if (!form.startDate) {
      next.startDate = 'Start date is required.'
    }
    if (!form.targetDate) {
      next.targetDate = 'Target date is required.'
    } else if (
      form.targetDate < todayIso &&
      !((isEdit || isCurrentPeriod) && form.targetDate === initialTargetDate)
    ) {
      // Past deadline is blocked on create for a PAST-period board, and on edit
      // UNLESS it's the goal's own unchanged already-past target. The current
      // period's unchanged pre-fill is also exempt (its working week may have
      // ended, e.g. on a weekend) — the server still enforces the period guard.
      next.targetDate = 'Target date cannot be in the past.'
    } else if (form.startDate && form.targetDate < form.startDate) {
      next.targetDate = 'Target date cannot be before the start date.'
    } else if (form.startDate) {
      // The span must match the goal's cadence, so it doesn't overlap several
      // period boards: a weekly goal fits in one week, a monthly goal in one
      // calendar month.
      if (type === 'monthly') {
        if (form.startDate.slice(0, 7) !== form.targetDate.slice(0, 7)) {
          next.targetDate = 'A monthly goal must stay within a single calendar month.'
        }
      } else if (daySpan(form.startDate, form.targetDate) > 6) {
        next.targetDate = 'A weekly goal must span a single week (up to 7 days).'
      }
    }
    // When assigning to someone else, a member must actually be chosen.
    if (showAssignee && assignMode === 'other' && !assignee) {
      next.assignee = 'Select a member to assign this goal to.'
    }
    // Every key result needs a numeric target — without one, progress can't be
    // measured (current ÷ target).
    const krs = form.keyResults ?? []
    if (krs.some((t) => !String(t.name ?? '').trim())) {
      // Key result name is mandatory — an unnamed KR shows only as "Number"/
      // "Percentage", which reads as a blank metric.
      next.keyResults = 'Enter a name for every key result.'
    } else if (krs.length && krs.some(targetInvalid)) {
      next.keyResults = 'Target must be greater than 0.'
    } else if (
      krs.some((t) =>
        ['start', 'target', 'current'].some((f) => {
          const v = num(t[f], null)
          return v !== null && v < 0
        }),
      )
    ) {
      // Measurable values are counts / percentages — they can't be negative.
      next.keyResults = 'Key result values cannot be negative.'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  function buildPayload() {
    return {
      name: form.name.trim(),
      description: form.description.trim() || null,
      priority: form.priority,
      startDate: form.startDate || undefined,
      targetDate: form.targetDate || undefined,
      manualStatus: form.manualStatus,
      // Only set when a Manager/Admin assigned the goal to a specific member;
      // otherwise omitted so the server defaults ownership to the creator.
      ownerId:
        showAssignee && assignMode === 'other' && assignee ? assignee.id : undefined,
      tags: form.tags ?? [],
      keyResults: (form.keyResults ?? []).map((t) => ({
        id: t.id,
        type: t.type,
        name: (t.name ?? '').trim(),
        start: num(t.start, 0),
        target: num(t.target, null),
        current: num(t.current, null),
      })),
    }
  }

  function submit(e) {
    e.preventDefault()
    if (!validate()) return
    onSubmit(buildPayload())
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={title}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="goal-form" loading={saving}>
            {submitLabel}
          </Button>
        </>
      }
    >
      {/* noValidate: suppress the browser's native constraint bubbles (e.g. the
          date input's "Value must be … or later") so our own styled inline
          errors from validate() are shown instead — matching the app's QA
          standard. The date `min` still greys out past dates in the picker. */}
      <form id="goal-form" onSubmit={submit} noValidate className="space-y-4">
        <TextField
          label="Goal Name"
          required
          value={form.name}
          error={errors.name}
          onChange={(e) => {
            setForm((f) => ({ ...f, name: e.target.value }))
            if (errors.name) setErrors((x) => ({ ...x, name: undefined }))
          }}
          placeholder="Enter a Goal Name"
          maxLength={100}
          autoFocus
        />
        <TextAreaField
          label="Description"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="Enter a Description"
          rows={2}
          maxLength={1000}
          // Surface the 1000-char cap (already enforced by maxLength + the DTO)
          // as a live "N/1000" counter, matching the rest of the app.
          showCount
        />

        {/* Assignee — create-only, Managers/Admins only. Two-step: department
         *  then a member of it. Defaults to the creator ("Myself"). */}
        {showAssignee ? (
          <div>
            <FieldLabel label="Assignee" />
            <div className="mb-2 inline-flex rounded-lg border border-[color:var(--color-rule)] p-0.5">
              {[
                { key: 'self', label: 'Myself' },
                { key: 'other', label: 'Someone else' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setAssignMode(opt.key)
                    if (opt.key === 'self') {
                      setAssignee(null)
                      setAssigneeDeptId('')
                    }
                    if (errors.assignee) setErrors((x) => ({ ...x, assignee: undefined }))
                  }}
                  className={
                    'rounded-md px-3 py-1 text-sm cursor-pointer ' +
                    (assignMode === opt.key
                      ? 'bg-[color:var(--color-accent-bg)] font-medium text-[color:var(--color-accent)]'
                      : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]')
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {assignMode === 'self' ? (
              <p className="text-xs text-[color:var(--color-ink-muted)]">
                This goal will be assigned to you.
              </p>
            ) : (
              <div className="space-y-2">
                {/* Step 1 — department */}
                <select
                  aria-label="Assignee department"
                  value={assigneeDeptId}
                  onChange={(e) => {
                    setAssigneeDeptId(e.target.value)
                    setAssignee(null)
                    setOwnerSearch('')
                  }}
                  className="h-10 w-full rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 text-sm text-[color:var(--color-ink)] cursor-pointer focus:outline-none focus:border-[color:var(--color-accent)]"
                >
                  <option value="">Select a department…</option>
                  {assignDepartments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                {/* Step 2 — member of that department */}
                {!assigneeDeptId ? (
                  <p className="text-xs text-[color:var(--color-ink-subtle)]">
                    Choose a department to see its members.
                  </p>
                ) : assignee ? (
                  <div className="flex items-center gap-2 rounded-lg border border-[color:var(--color-rule)] px-3 py-2">
                    <Avatar name={assignee.fullName} size="sm" colorByName tooltip={false} />
                    <span className="min-w-0 flex-1 truncate text-sm text-[color:var(--color-ink)]">
                      {assignee.fullName}
                    </span>
                    <button
                      type="button"
                      onClick={() => setAssignee(null)}
                      aria-label="Clear selected assignee"
                      className="cursor-pointer rounded p-1 text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-priority-urgent)]"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="rounded-lg border border-[color:var(--color-rule)]">
                    <div className="border-b border-[color:var(--color-rule)] p-2">
                      <div className="relative">
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
                        >
                          <Search size={14} />
                        </span>
                        <input
                          type="text"
                          value={ownerSearch}
                          onChange={(e) => setOwnerSearch(e.target.value)}
                          placeholder="Search by Name or Email ID"
                          className="fd-search-pill w-full rounded-full py-1.5 pl-9 pr-3 text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-subtle)] focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="max-h-52 overflow-auto">
                      {ownersQuery.isLoading ? (
                        <p className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
                      ) : ownerCandidates.length === 0 ? (
                        <p className="px-3 py-3 text-sm text-[color:var(--color-ink-muted)]">
                          {ownerSearchDebounced
                            ? 'No matches found.'
                            : 'No members available to assign to.'}
                        </p>
                      ) : (
                        ownerCandidates.map((u) => (
                          <div
                            key={u.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setAssignee({ id: u.id, fullName: u.fullName })
                              if (errors.assignee) setErrors((x) => ({ ...x, assignee: undefined }))
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setAssignee({ id: u.id, fullName: u.fullName })
                                if (errors.assignee) setErrors((x) => ({ ...x, assignee: undefined }))
                              }
                            }}
                            className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-[color:var(--color-card-hover)]"
                          >
                            <Avatar name={u.fullName} size="sm" colorByName tooltip={false} />
                            <span className="min-w-0 flex-1 text-left">
                              <span className="block truncate">{u.fullName}</span>
                              {u.workRoleName || u.departmentName ? (
                                <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
                                  {u.workRoleName ? (
                                    <span className="min-w-0 break-all text-xs text-[color:var(--color-ink-muted)]">
                                      {u.workRoleName}
                                    </span>
                                  ) : null}
                                  {u.departmentName ? (
                                    <span className="inline-flex min-w-0 items-center rounded border border-[color:var(--color-rule)] bg-[color:var(--color-card-hover)] px-1.5 py-0.5 text-[10px] font-medium text-[color:var(--color-ink)]">
                                      {u.departmentName}
                                    </span>
                                  ) : null}
                                </span>
                              ) : null}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
                {errors.assignee ? (
                  <p role="alert" className="text-xs text-[color:var(--color-priority-urgent)]">
                    {errors.assignee}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        ) : null}

        {/* Key results — Number / Percentage (Currency intentionally not
         *  offered; see TARGET_TYPES in lib/goals-ui.jsx). */}
        <div>
          <FieldLabel label="Key Results" />
          {form.keyResults?.length ? (
            <ul className="mb-2 space-y-2">
              {form.keyResults.map((t, i) => (
                <li key={i} className="rounded-lg border border-[color:var(--color-rule)] p-2.5">
                  <span className="mb-0.5 block text-xs text-[color:var(--color-ink-muted)]">
                    Name<span className="text-[color:var(--color-priority-urgent)]">*</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-[color:var(--color-card-hover)] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[color:var(--color-ink-muted)]">
                      {TARGET_LABEL[t.type]}
                    </span>
                    <input
                      value={t.name ?? ''}
                      onChange={(e) => updateKr(i, { name: e.target.value })}
                      placeholder="Enter a key result name"
                      maxLength={120}
                      aria-label="Key result name"
                      className={
                        'flex-1 rounded-md border bg-[color:var(--color-canvas)] px-2 py-1 text-sm text-[color:var(--color-ink)] focus:outline-none ' +
                        (errors.keyResults && !String(t.name ?? '').trim()
                          ? 'border-[color:var(--color-priority-urgent)] focus:border-[color:var(--color-priority-urgent)]'
                          : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
                      }
                    />
                    <button
                      type="button"
                      onClick={() => removeKr(i)}
                      aria-label="Remove key result"
                      className="rounded p-1 text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-priority-urgent)] cursor-pointer"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {errors.keyResults && !String(t.name ?? '').trim() ? (
                    <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                      Enter a name for every key result.
                    </p>
                  ) : null}
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {['start', 'target', 'current'].map((field) => {
                      const invalidTarget =
                        field === 'target' && errors.keyResults && targetInvalid(t)
                      const krFieldId = `${uid}-kr-${i}-${field}`
                      return (
                        <div key={field}>
                          {/* Unwrapped from a `<label>` that WRAPPED this input. A wrapping
                              label associates implicitly, so its ENTIRE box forwards clicks to
                              the control — and as a GRID ITEM this label is blockified and
                              stretched to the full column (124px measured) while "start" /
                              "target" / "current" are only 27-43px, leaving ~81-97px per column
                              that looked like empty space but focused the field (QA). Same shape
                              `FieldLabel` (ui/Field.jsx) uses: a `block w-fit` label + `htmlFor`. */}
                          <label
                            htmlFor={krFieldId}
                            className="mb-0.5 block w-fit capitalize text-xs text-[color:var(--color-ink-muted)]"
                          >
                            {field}
                            {field === 'target' ? (
                              <span className="text-[color:var(--color-priority-urgent)]">*</span>
                            ) : null}
                          </label>
                          <input
                            id={krFieldId}
                            type="number"
                            min="0"
                            value={t[field] ?? ''}
                            onChange={(e) => updateKr(i, { [field]: e.target.value })}
                            aria-invalid={invalidTarget ? true : undefined}
                            className={
                              'w-full rounded-md border bg-[color:var(--color-canvas)] px-2 py-1 text-sm text-[color:var(--color-ink)] focus:outline-none ' +
                              (invalidTarget
                                ? 'border-[color:var(--color-priority-urgent)] focus:border-[color:var(--color-priority-urgent)]'
                                : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
                            }
                          />
                          {/* Validation message sits directly UNDER the Target
                           *  input (not below the whole 3-column grid) so it
                           *  aligns with the field it refers to. */}
                          {invalidTarget ? (
                            <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                              Target must be greater than 0.
                            </p>
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-[color:var(--color-ink-muted)]">Add key result:</span>
            {TARGET_TYPES.map((tt) => (
              <button
                key={tt.key}
                type="button"
                onClick={() => addKr(tt.key)}
                title={tt.hint}
                className="rounded-full border border-[color:var(--color-rule)] px-2 py-0.5 text-xs text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
              >
                {tt.label}
              </button>
            ))}
          </div>
          {/* Section-level fallback for KR errors not tied to a specific field
           *  (e.g. negative numeric values). Name + target errors are shown inline
           *  under their own KR fields above. */}
          {errors.keyResults &&
          !(form.keyResults ?? []).some((t) => !String(t.name ?? '').trim()) &&
          !(form.keyResults ?? []).some(targetInvalid) ? (
            <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {errors.keyResults}
            </p>
          ) : null}
        </div>

        {/* Tags — free-form labels (lowercased, de-duped on save). */}
        <TagsField
          value={form.tags ?? []}
          onChange={(next) => setForm((f) => ({ ...f, tags: next }))}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel label="Priority" htmlFor="goal-priority" />
            <select
              id="goal-priority"
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              className="h-10 w-full rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 text-sm text-[color:var(--color-ink)] cursor-pointer focus:outline-none focus:border-[color:var(--color-accent)]"
            >
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <FieldLabel label="Status" htmlFor="goal-status" />
            <select
              id="goal-status"
              value={form.manualStatus}
              onChange={(e) => setForm((f) => ({ ...f, manualStatus: e.target.value }))}
              disabled={(form.keyResults?.length ?? 0) > 0}
              title={(form.keyResults?.length ?? 0) > 0 ? 'Status is derived from this goal’s key results.' : undefined}
              className="h-10 w-full rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 text-sm text-[color:var(--color-ink)] cursor-pointer focus:outline-none focus:border-[color:var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <DateField
            label="Start Date"
            required
            value={form.startDate}
            error={errors.startDate}
            onChange={(e) => {
              setForm((f) => ({ ...f, startDate: e.target.value }))
              if (errors.startDate || errors.targetDate) {
                setErrors((x) => ({ ...x, startDate: undefined, targetDate: undefined }))
              }
            }}
          />
          <DateField
            label="Target Date"
            required
            min={targetMinIso}
            value={form.targetDate}
            error={errors.targetDate}
            onChange={(e) => {
              setForm((f) => ({ ...f, targetDate: e.target.value }))
              if (errors.targetDate) setErrors((x) => ({ ...x, targetDate: undefined }))
            }}
          />
        </div>
      </form>
    </Modal>
  )
}

/**
 * Free-form tag editor. Type a tag and press Enter or comma to add it; tags are
 * normalised (lowercase, no leading '#', de-duplicated) to match the server.
 */
// Mirror of the server cap (goals.dto.js: max 20 tags, 40 chars each). The
// backend re-enforces both, so this is just the friendly front-of-house guard.
const MAX_TAGS = 20
const MAX_TAG_LEN = 40

function TagsField({ value, onChange }) {
  const [draft, setDraft] = useState('')
  // "Max 20 tags" shown inline BELOW the field (consistent with the rest of the
  // form) rather than as a toast. Cleared once the user removes a tag or adds one.
  const [limitError, setLimitError] = useState('')
  const atLimit = value.length >= MAX_TAGS

  const commit = (raw) => {
    const tag = raw.replace(/^#+/, '').trim().toLowerCase().slice(0, MAX_TAG_LEN)
    if (!tag) return
    if (value.includes(tag)) {
      // Surface a duplicate-tag message instead of silently swallowing it
      // (previously it just cleared the input, so the user got no feedback).
      setLimitError(`"${tag}" is already added.`)
      setDraft('')
      return
    }
    if (value.length >= MAX_TAGS) {
      setLimitError(`A goal can have at most ${MAX_TAGS} tags.`)
      return
    }
    onChange([...value, tag])
    setDraft('')
    setLimitError('')
  }

  return (
    <div>
      <FieldLabel label="Tags" htmlFor="goal-tags" />
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-2 py-1.5 focus-within:border-[color:var(--color-accent)]">
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-accent-bg)] px-2 py-0.5 text-xs font-medium text-[color:var(--color-accent-strong)]"
          >
            #{tag}
            <button
              type="button"
              onClick={() => {
                onChange(value.filter((t) => t !== tag))
                setLimitError('')
              }}
              aria-label={`Remove ${tag}`}
              className="cursor-pointer text-[color:var(--color-accent-strong)] hover:opacity-70"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          id="goal-tags"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault()
              commit(draft)
            } else if (e.key === 'Backspace' && draft === '' && value.length) {
              onChange(value.slice(0, -1))
            }
          }}
          onBlur={() => commit(draft)}
          placeholder={
            atLimit ? `Tag limit reached (${MAX_TAGS})` : value.length ? 'Add another…' : 'e.g. dashboard, fy26'
          }
          maxLength={MAX_TAG_LEN}
          disabled={atLimit}
          className="min-w-[8rem] flex-1 bg-transparent px-1 py-0.5 text-sm text-[color:var(--color-ink)] focus:outline-none disabled:cursor-not-allowed"
        />
      </div>
      <div className="mt-1 flex items-start justify-between gap-2 text-xs">
        {limitError ? (
          <p role="alert" className="text-[color:var(--color-priority-urgent)]">
            {limitError}
          </p>
        ) : (
          <p className="text-[color:var(--color-ink-subtle)]">
            Press Enter to add a tag. Used to group and filter goals.
          </p>
        )}
        {/* Live count surfaces the cap that the input + server both enforce. */}
        <span
          className={
            'shrink-0 tabular-nums ' +
            (atLimit ? 'text-[color:var(--color-priority-urgent)]' : 'text-[color:var(--color-ink-subtle)]')
          }
        >
          {value.length}/{MAX_TAGS}
        </span>
      </div>
    </div>
  )
}
